// W39 — Globális kereső: feladat, projekt és ember egy helyen.
//
// Két, szándékosan szétválasztott rész:
//   - `searchAll` tiszta függvény (DOM nélkül, így hálózat és böngésző nélkül
//     is tesztelhető) — csoportosított találatokat ad vissza,
//   - `searchOverlay` a Ctrl+K-ra nyíló panel, ami ezt használja.
//
// A keresés ékezet- és kisbetű-független (NFD): a felhasználó a keresőbe
// ritkán gépel ékezetet, enélkül a "kazancsere" nem találná meg a
// "Kazáncserét".

import { t } from '../lib/i18n';
import { avatar, emptyState, statusBadge } from '../lib/components';
import { h, icon } from '../lib/dom';
import type { PersonView, ProjectView } from '../lib/data';
import type { TaskView } from '../lib/model';
import { normalize } from './people';
import { completedCount } from './projects';
import { attachmentName } from './task-detail';

// ---------------------------------------------------------------------------
// Adatszerkezetek
// ---------------------------------------------------------------------------

export type SearchKind = 'task' | 'project' | 'person' | 'file';

/// A panel fölötti szűrőcsipeszek: a `all` jelenti a "Mind"-et.
export type SearchFilterKind = 'all' | SearchKind;

type HitBase = {
  id: string;
  title: string;
  /// A cím alatti magyarázó sor (projekt, haladás, munkakör).
  subtitle: string;
  /// Találati pontszám — a rendezés alapja.
  score: number;
};

export type TaskHit = HitBase & { kind: 'task'; task: TaskView };
export type ProjectHit = HitBase & { kind: 'project'; project: ProjectView };
export type PersonHit = HitBase & { kind: 'person'; person: PersonView };
/// Csatolmány-találat. A fájl nevét a Storage-hivatkozásból olvassuk
/// vissza (a modell nem tárolja); a megnyitás magát a feladatot nyitja.
export type FileHit = HitBase & { kind: 'file'; task: TaskView; url: string };

export type SearchHit = TaskHit | ProjectHit | PersonHit | FileHit;

export type SearchSources = {
  tasks: TaskView[];
  projects: ProjectView[];
  people: PersonView[];
};

export type SearchResults = {
  /// A levágott, normalizálatlan keresőkifejezés — a felületen ezt mutatjuk.
  query: string;
  tasks: TaskHit[];
  projects: ProjectHit[];
  people: PersonHit[];
  files: FileHit[];
  total: number;
};

/// Csoportonként ennyi találatot mutatunk; a panel nem egy második
/// feladatlista, hanem gyors ugrás a keresett dologra.
const groupLimit = 8;

// ---------------------------------------------------------------------------
// A tiszta kereső
// ---------------------------------------------------------------------------

type Field = { value: string | undefined; weight: number };

/// Egy mező pontszáma: a teljes egyezés ér a legtöbbet, utána a szókezdet,
/// végül a szó belsejében lévő találat.
function fieldScore(value: string | undefined, weight: number, needle: string): number {
  if (!value) return 0;
  const haystack = normalize(value);
  if (haystack.length === 0) return 0;

  const at = haystack.indexOf(needle);
  if (at < 0) return 0;

  if (haystack === needle) return weight * 4;
  if (at === 0) return weight * 3;
  if (haystack[at - 1] === ' ' || haystack[at - 1] === '-') return weight * 2;
  return weight;
}

function scoreOf(fields: Field[], needle: string): number {
  let best = 0;
  for (const field of fields) {
    best = Math.max(best, fieldScore(field.value, field.weight, needle));
  }
  return best;
}

/// Pontszám szerint csökkenő, azon belül magyar ábécérend — így azonos
/// erősségű találatoknál sem ugrál a lista.
function byScoreThenTitle(a: HitBase, b: HitBase): number {
  if (b.score !== a.score) return b.score - a.score;
  return a.title.localeCompare(b.title, 'hu');
}

/// Globális keresés a már betöltött adatokon.
///
/// Üres (vagy csak szóközből álló) keresésre üres eredményt ad: a panel
/// ilyenkor nem dob a felhasználó elé több száz sort.
export function searchAll(
  query: string,
  sources: SearchSources,
  options: { limitPerGroup?: number } = {},
): SearchResults {
  const trimmed = query.trim();
  const needle = normalize(trimmed);
  const limit = options.limitPerGroup ?? groupLimit;

  if (needle.length === 0) {
    return { query: trimmed, tasks: [], projects: [], people: [], files: [], total: 0 };
  }

  const tasks: TaskHit[] = [];
  for (const task of sources.tasks) {
    const sequence = task.sequenceNumber !== undefined ? String(task.sequenceNumber) : undefined;
    const score = scoreOf(
      [
        { value: task.title, weight: 10 },
        { value: task.projectTitle, weight: 6 },
        { value: sequence, weight: 6 },
        { value: task.description, weight: 3 },
      ],
      needle,
    );
    if (score === 0) continue;

    tasks.push({
      kind: 'task',
      id: task.id,
      title: task.title,
      subtitle: [sequence ? `${sequence}. feladat` : undefined, task.projectTitle]
        .filter((part): part is string => Boolean(part))
        .join(' · '),
      score,
      task,
    });
  }

  const projects: ProjectHit[] = [];
  for (const project of sources.projects) {
    const score = scoreOf(
      [
        { value: project.title, weight: 10 },
        { value: project.description, weight: 3 },
      ],
      needle,
    );
    if (score === 0) continue;

    projects.push({
      kind: 'project',
      id: project.id,
      title: project.title,
      subtitle: t('search.projectProgress', [
        String(completedCount(project)),
        String(project.tasks.length),
      ]),
      score,
      project,
    });
  }

  const people: PersonHit[] = [];
  for (const person of sources.people) {
    const score = scoreOf(
      [
        { value: person.fullName, weight: 10 },
        { value: person.jobTitle, weight: 4 },
      ],
      needle,
    );
    if (score === 0) continue;

    people.push({
      kind: 'person',
      id: person.id,
      title: person.fullName,
      subtitle: person.jobTitle ?? t('search.noJobTitle'),
      score,
      person,
    });
  }

  // A terv „Fajl" szurojehez: a feladatok csatolmanyai kozott is keresunk.
  // A fajlnev a Storage-utvonalbol jon vissza — a modell nem tarolja.
  const files: FileHit[] = [];
  for (const task of sources.tasks) {
    for (const attachment of task.attachments) {
      if (!attachment.url) continue;
      const name = attachmentName(attachment.url);
      if (name.length === 0) continue;

      const score = scoreOf([{ value: name, weight: 10 }], needle);
      if (score === 0) continue;

      files.push({
        kind: 'file',
        id: attachment.url,
        title: name,
        subtitle: [
          task.sequenceNumber ? `#${task.sequenceNumber}` : null,
          task.title,
          attachment.source === 'assigned' ? t('search.sourceAssigned') : t('search.sourceFinished'),
        ]
          .filter((part): part is string => Boolean(part))
          .join(' · '),
        score,
        task,
        url: attachment.url,
      });
    }
  }

  const cut = <T extends HitBase>(hits: T[]): T[] => hits.sort(byScoreThenTitle).slice(0, limit);

  const cutTasks = cut(tasks);
  const cutProjects = cut(projects);
  const cutPeople = cut(people);
  const cutFiles = cut(files);

  return {
    query: trimmed,
    tasks: cutTasks,
    projects: cutProjects,
    people: cutPeople,
    files: cutFiles,
    total: cutTasks.length + cutProjects.length + cutPeople.length + cutFiles.length,
  };
}

type Group = { key: SearchKind; label: string; items: SearchHit[] };

/// A csoportok megjelenítési sorrendje — a lapozás is ezt követi.
function groupsOf(results: SearchResults, kind: SearchFilterKind): Group[] {
  const all: Group[] = [
    { key: 'task', label: 'Feladatok', items: results.tasks },
    { key: 'project', label: 'Projektek', items: results.projects },
    { key: 'person', label: 'Emberek', items: results.people },
    { key: 'file', label: t('search.groupFiles'), items: results.files },
  ];
  return all.filter((group) => (kind === 'all' || kind === group.key) && group.items.length > 0);
}

/// A csoportosított találatok egyetlen listává lapítva — a nyilas navigáció
/// ezen a sorrenden lépked.
export function flattenHits(
  results: SearchResults,
  kind: SearchFilterKind = 'all',
): SearchHit[] {
  return groupsOf(results, kind).flatMap((group) => group.items);
}

// ---------------------------------------------------------------------------
// W39 — a Ctrl+K panel
// ---------------------------------------------------------------------------

const searchIconPath =
  'M11.6 7.2a4.4 4.4 0 10-8.8 0 4.4 4.4 0 008.8 0M10.6 10.6L14 14';

const kindIconPath: Record<SearchKind, string> = {
  task: 'M4.6 2.6h6.8a2 2 0 012 2v6.8a2 2 0 01-2 2H4.6a2 2 0 01-2-2V4.6a2 2 0 012-2zM5.4 8.2l1.8 1.8 3.4-3.6',
  project:
    'M2.4 4.6a1.4 1.4 0 011.4-1.4h2.4l1.4 1.8h4.6a1.4 1.4 0 011.4 1.4v5.2a1.4 1.4 0 01-1.4 1.4H3.8a1.4 1.4 0 01-1.4-1.4z',
  person:
    'M6.2 3.8a2.4 2.4 0 100 4.8 2.4 2.4 0 000-4.8M2.2 13a4.2 4.2 0 018 0M10.6 4.2a2.4 2.4 0 010 4.4M11.4 13a4.3 4.3 0 00-1.2-2.9',
  file: 'M11.4 6.2l-4.6 4.6a2 2 0 002.8 2.8l4.6-4.6a3.4 3.4 0 00-4.8-4.8L4.2 9.4',
};

/// A szurocsipek feliratainak KULCSAI. Kesz szoveget nem tarolhat modul
/// szintu tabla: a szotar csak a render elott erkezik meg.
const filterChips: { key: SearchFilterKind; labelKey: string }[] = [
  { key: 'all', labelKey: 'search.kindAll' },
  { key: 'task', labelKey: 'search.kindTask' },
  { key: 'project', labelKey: 'search.kindProject' },
  { key: 'person', labelKey: 'search.kindPerson' },
  { key: 'file', labelKey: 'search.kindFile' },
];

export type SearchOverlayOptions = {
  /// Az adatforrás. Függvényként megadva mindig a friss listát kapjuk —
  /// a panel újranyitásakor nem ragad be a betöltéskori állapot.
  sources: SearchSources | (() => SearchSources);
  /// A kiválasztott találat. A panel előbb bezárul, utána hívjuk.
  onSelect: (hit: SearchHit) => void;
  /// Ctrl+K (macOS-en ⌘K) globális gyorsbillentyű; alapból be van kapcsolva.
  shortcut?: boolean;
  placeholder?: string;
  onOpened?: () => void;
  onClosed?: () => void;
};

export type SearchOverlayHandle = {
  open: (query?: string) => void;
  close: () => void;
  isOpen: () => boolean;
  /// A globális billentyűfigyelő leszerelése (pl. kijelentkezéskor).
  dispose: () => void;
};

const focusableSelector = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

/// A globális kereső panel.
///
/// Nem rajzol ki semmit, amíg nem nyitják meg: a visszaadott vezérlővel
/// (`open`) vagy Ctrl+K-val jön elő. Esc zárja, a nyilak léptetnek, az Enter
/// választ — pontosan úgy, ahogy a W39 terv lábléce ígéri.
export function searchOverlay(options: SearchOverlayOptions): SearchOverlayHandle {
  const panelId = `search-${Math.random().toString(36).slice(2, 8)}`;
  const listId = `${panelId}-list`;

  let overlay: HTMLElement | null = null;
  let previouslyFocused: HTMLElement | null = null;
  let kind: SearchFilterKind = 'all';
  let hits: SearchHit[] = [];
  let activeIndex = 0;
  let rowElements: HTMLElement[] = [];

  function currentSources(): SearchSources {
    return typeof options.sources === 'function' ? options.sources() : options.sources;
  }

  const input = h('input', {
    class: 'body-lg',
    type: 'search',
    role: 'combobox',
    autocomplete: 'off',
    spellcheck: 'false',
    'aria-expanded': 'true',
    'aria-controls': listId,
    'aria-label': t('search.inputLabel'),
    placeholder: options.placeholder ?? t('search.placeholder'),
    style: {
      flexGrow: '1',
      minWidth: '0',
      height: '34px',
      border: '0',
      background: 'transparent',
      color: 'var(--tp)',
      font: 'inherit',
      fontSize: '17px',
    },
    onInput: () => {
      activeIndex = 0;
      renderResults();
    },
  });

  const chipsHost = h('div', {
    class: 'row g6 wrap',
    style: { padding: '10px 18px', borderBottom: '1px solid var(--border)' },
  });

  const listHost = h('div', {
    id: listId,
    role: 'listbox',
    'aria-label': t('search.resultsLabel'),
    style: { maxHeight: '420px', overflow: 'auto' },
  });

  const countLabel = h('span', {
    class: 'caption',
    style: { marginLeft: 'auto' },
    'aria-live': 'polite',
    text: '',
  });

  function renderChips(): void {
    chipsHost.replaceChildren(
      ...filterChips.map((chip) =>
        h('button', {
          type: 'button',
          class: `chip${kind === chip.key ? ' on' : ''}`,
          'aria-pressed': kind === chip.key ? 'true' : 'false',
          style: { font: 'inherit', cursor: 'pointer' },
          text: t(chip.labelKey),
          onClick: () => {
            kind = chip.key;
            activeIndex = 0;
            renderChips();
            renderResults();
            input.focus();
          },
        }),
      ),
    );
  }

  function iconBox(path: string): HTMLElement {
    return h(
      'span',
      {
        class: 'ic',
        style: {
          width: '32px',
          height: '32px',
          borderRadius: '8px',
          background: 'var(--sunken)',
          color: 'var(--ts)',
        },
      },
      icon(path, 16),
    );
  }

  function hitRow(hit: SearchHit, index: number): HTMLElement {
    const row = h(
      'button',
      {
        type: 'button',
        id: `${listId}-opt-${index}`,
        role: 'option',
        tabindex: '-1',
        'aria-selected': 'false',
        class: 'lrow',
        style: {
          width: '100%',
          border: '0',
          textAlign: 'left',
          font: 'inherit',
          cursor: 'pointer',
        },
        onClick: () => choose(index),
        onMousemove: () => {
          if (activeIndex === index) return;
          activeIndex = index;
          applyActive();
        },
      },
      hit.kind === 'person' ? avatar(hit.person.fullName, 'sm') : iconBox(kindIconPath[hit.kind]),
      h(
        'span',
        { class: 'col grow', style: { gap: '2px', minWidth: '0' } },
        h('span', {
          class: 'body-sm w6',
          style: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
          text: hit.title,
        }),
        h('span', {
          class: 'caption',
          style: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
          text: hit.subtitle,
        }),
      ),
      hit.kind === 'task' ? statusBadge(hit.task.status) : null,
    );
    return row;
  }

  function applyActive(): void {
    rowElements.forEach((row, index) => {
      const on = index === activeIndex;
      row.classList.toggle('on', on);
      row.setAttribute('aria-selected', on ? 'true' : 'false');
    });

    const active = rowElements[activeIndex];
    if (active) {
      active.scrollIntoView({ block: 'nearest' });
      input.setAttribute('aria-activedescendant', active.id);
    } else {
      input.removeAttribute('aria-activedescendant');
    }
  }

  function renderResults(): void {
    const results = searchAll(input.value, currentSources());
    const groups = groupsOf(results, kind);
    hits = groups.flatMap((group) => group.items);

    if (activeIndex >= hits.length) activeIndex = Math.max(0, hits.length - 1);
    rowElements = [];

    if (results.query.length === 0) {
      countLabel.textContent = '';
      listHost.replaceChildren(
        emptyState({
          title: 'Mit keresel?',
          text: t('search.typeToSearch'),
        }),
      );
      applyActive();
      return;
    }

    countLabel.textContent = t('search.hitCount', [String(hits.length)]);

    if (hits.length === 0) {
      listHost.replaceChildren(
        emptyState({
          title: t('search.noHitTitle'),
          text: t('search.noHitText', [results.query]),
        }),
      );
      applyActive();
      return;
    }

    const nodes: HTMLElement[] = [];
    let index = 0;
    for (const group of groups) {
      nodes.push(
        h(
          'div',
          { style: { padding: '12px 18px 4px' } },
          h('span', { class: 'label', text: group.label }),
        ),
      );
      for (const hit of group.items) {
        const row = hitRow(hit, index);
        rowElements.push(row);
        nodes.push(row);
        index += 1;
      }
    }

    listHost.replaceChildren(...nodes);
    applyActive();
  }

  function move(delta: number): void {
    if (hits.length === 0) return;
    // Körbeér: a lista aljáról a tetejére lép, ahogy a parancspalettáknál szokás.
    activeIndex = (activeIndex + delta + hits.length) % hits.length;
    applyActive();
  }

  function choose(index: number): void {
    const hit = hits[index];
    if (!hit) return;
    close();
    options.onSelect(hit);
  }

  function onKeyDown(event: KeyboardEvent): void {
    if (!overlay) return;

    switch (event.key) {
      case 'Escape':
        event.preventDefault();
        event.stopPropagation();
        close();
        return;
      case 'ArrowDown':
        event.preventDefault();
        move(1);
        return;
      case 'ArrowUp':
        event.preventDefault();
        move(-1);
        return;
      case 'Home':
        if (hits.length === 0) return;
        event.preventDefault();
        activeIndex = 0;
        applyActive();
        return;
      case 'End':
        if (hits.length === 0) return;
        event.preventDefault();
        activeIndex = hits.length - 1;
        applyActive();
        return;
      case 'Enter':
        if (hits.length === 0) return;
        event.preventDefault();
        choose(activeIndex);
        return;
      case 'Tab':
        break;
      default:
        return;
    }

    // Tab: a fókusz a panelen belül marad (szűrőcsipeszek ↔ kereső).
    const focusable = [...(overlay.querySelectorAll<HTMLElement>(focusableSelector))].filter(
      (el) => el.offsetParent !== null,
    );
    if (focusable.length === 0) return;

    const first = focusable[0]!;
    const last = focusable[focusable.length - 1]!;
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  function buildPanel(): HTMLElement {
    return h(
      'div',
      {
        class: 'dialog',
        role: 'dialog',
        'aria-modal': 'true',
        'aria-label': t('search.globalSearch'),
        style: {
          width: '680px',
          maxWidth: '100%',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        },
      },
      h(
        'div',
        {
          class: 'row g12',
          style: { padding: '14px 18px', borderBottom: '1px solid var(--border)' },
        },
        h('span', { class: 'ic tm' }, icon(searchIconPath, 19)),
        input,
        h('span', { class: 'kbd', text: 'Esc' }),
      ),
      chipsHost,
      listHost,
      h(
        'div',
        {
          class: 'row g16',
          style: { padding: '10px 18px', borderTop: '1px solid var(--border)' },
        },
        h(
          'span',
          { class: 'caption' },
          h('span', { class: 'kbd', text: '↑' }),
          h('span', { text: ' ' }),
          h('span', { class: 'kbd', text: '↓' }),
          h('span', { text: t('search.navigate') }),
        ),
        h(
          'span',
          { class: 'caption' },
          h('span', { class: 'kbd', text: '↵' }),
          h('span', { text: t('search.open') }),
        ),
        countLabel,
      ),
    );
  }

  function open(query?: string): void {
    if (overlay) {
      input.focus();
      input.select();
      return;
    }

    previouslyFocused = document.activeElement as HTMLElement | null;

    if (query !== undefined) input.value = query;
    kind = 'all';
    activeIndex = 0;
    renderChips();

    const panel = buildPanel();
    overlay = h(
      'div',
      {
        // A ds.css tokenjeit az `.app` osztaly hordozza a `#root`-on; ez a
        // fedoreteg viszont a `<body>` ala kerul, ahol egyetlen token sem
        // oldodna fel — a panel keret es hatter nelkul jelent volna meg.
        // (Ugyanaz a hiba, ami a parbeszedablakoknal is megvolt.)
        class: `app${document.querySelector('#root')?.classList.contains('dark') ? ' dark' : ''} scrim dialog-overlay`,
        // A terv szerint a panel fölül nyílik, nem képernyő-közepén.
        style: { alignItems: 'flex-start', paddingTop: '80px' },
        onClick: (event: MouseEvent) => {
          if (event.target === overlay) close();
        },
      },
      panel,
    );

    document.body.append(overlay);
    // A háttér a képernyőolvasó elől elrejtve, amíg a panel nyitva van.
    document.querySelector<HTMLElement>('#root')?.setAttribute('aria-hidden', 'true');
    document.addEventListener('keydown', onKeyDown, true);

    renderResults();
    input.focus();
    input.select();
    options.onOpened?.();
  }

  function close(): void {
    if (!overlay) return;
    document.removeEventListener('keydown', onKeyDown, true);
    overlay.remove();
    overlay = null;
    rowElements = [];
    hits = [];
    document.querySelector<HTMLElement>('#root')?.removeAttribute('aria-hidden');
    // A fókusz oda tér vissza, ahonnan nyitottuk.
    previouslyFocused?.focus?.();
    previouslyFocused = null;
    options.onClosed?.();
  }

  function onShortcut(event: KeyboardEvent): void {
    if (!(event.ctrlKey || event.metaKey) || event.altKey) return;
    if (event.key.toLowerCase() !== 'k') return;
    event.preventDefault();
    if (overlay) close();
    else open();
  }

  const shortcutEnabled = options.shortcut !== false;
  if (shortcutEnabled) document.addEventListener('keydown', onShortcut, true);

  return {
    open,
    close,
    isOpen: () => overlay !== null,
    dispose: () => {
      close();
      if (shortcutEnabled) document.removeEventListener('keydown', onShortcut, true);
    },
  };
}
